
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import { RunnitsOwnerSelection, RunnitState } from '@/store';
import { RUNNITS_ACCESS_LEVEL, Runnit, User, Team, RUNNIT_TYPE } from '@run-diffusion/shared';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { db, Query } from '@/firebase';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { getRunnitBoardTemplatesFilter, setRunnitBoardTemplatesFilter } from '@/utils/localStorage';
import { buildFeaturedTemplatesQuery, FEATURED_TEMPLATES_GRID_LIMIT } from '@/utils/featuredTemplates';
import { BoardsFilter } from '@/views/Runnits/pages/RunnitBoardsPage/types';
import RunnitTemplatesManagement from '@/views/Runnits/pages/RunnitTemplatesPage/RunnitTemplatesDashboard/RunnitTemplatesManagement.vue';
import RunnitTemplatesOnboardingBanner from '@/views/Runnits/pages/RunnitTemplatesPage/RunnitTemplatesDashboard/RunnitTemplatesOnboardingBanner.vue';
import RunnitTemplatesOnboardingDialog from '@/views/Runnits/pages/RunnitTemplatesPage/RunnitTemplatesDashboard/RunnitTemplatesOnboardingDialog.vue';

export default Vue.extend({
	name: 'RunnitTemplatesDashboard',
	mixins: [
		TeamUserMixin,
	],
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			RUNNITS_ACCESS_LEVEL,
			showOnboardingDialog: false,
			loadingTeamPrivateRunnits: false,
			loadingTeamRestrictedRunnits: false,
			loadingTeamSharedRunnits: false,
			loadingPersonalRunnits: false,
			loadingFeaturedRunnits: false,
			teamPrivateRunnits: [] as Runnit[],
			teamRestrictedRunnits: [] as Runnit[],
			teamSharedRunnits: [] as Runnit[],
			personalRunnits: [] as Runnit[],
			featuredRunnits: [] as Runnit[],
			templatesFilter: getRunnitBoardTemplatesFilter() as BoardsFilter,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'team',
		]),
		computedBoardsFilterValue (): BoardsFilter {
			if (this.templatesFilter === BoardsFilter.EVERYONES_BOARDS) {
				return this.hasManageAllGenerationsPermission ? this.templatesFilter : BoardsFilter.ALL;
			}
			return this.templatesFilter || BoardsFilter.ALL;
		},
		allRunnits (): Runnit[] {
			// Return featured templates when FEATURED_TEMPLATES filter is active
			if (this.templatesFilter === BoardsFilter.FEATURED_TEMPLATES) {
				return this.featuredRunnits;
			}
			// Otherwise return regular templates
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return [
					...this.teamPrivateRunnits,
					...this.teamRestrictedRunnits,
					...this.teamSharedRunnits,
				];
			} else {
				return this.personalRunnits;
			}
		},
		loadingAllRunnits (): boolean {
			// Return loading state for featured templates when FEATURED_TEMPLATES filter is active
			if (this.templatesFilter === BoardsFilter.FEATURED_TEMPLATES) {
				return this.loadingFeaturedRunnits;
			}
			// Otherwise return loading state for regular templates
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return !!(
					this.loadingTeamPrivateRunnits ||
					this.loadingTeamRestrictedRunnits ||
					this.loadingTeamSharedRunnits
				);
			} else {
				return this.loadingPersonalRunnits;
			}
		},
	},
	watch: {
		runnitState: {
			immediate: false,
			handler (newVal: RunnitState, oldVal: RunnitState) {
				const newOwnerSelection: RunnitsOwnerSelection = newVal?.runnitsOwnerSelection;
				const oldOwnerSelection: RunnitsOwnerSelection = oldVal?.runnitsOwnerSelection;

				if (oldOwnerSelection && newOwnerSelection && newOwnerSelection !== oldOwnerSelection) {
					// Only trigger reload on change, not on initial load
					this.loadRunnits(this.computedBoardsFilterValue, newOwnerSelection, this.user, this.team);
				}
			},
		},
		user: {
			immediate: true,
			handler (newVal: User, oldVal: User) {
				const newUserId: string = newVal?.id;
				const oldUserId: string = oldVal?.id;
				if (newUserId !== oldUserId) {
					this.loadRunnits(this.computedBoardsFilterValue, this.runnitState.runnitsOwnerSelection, newVal, this.team);
				}
			},
		},
		team: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id;
				const oldTeamId: string = oldVal?.id;
				if (newTeamId !== oldTeamId) {
					this.loadRunnits(this.computedBoardsFilterValue, this.runnitState.runnitsOwnerSelection, this.user, newVal);
				}
			},
		},
		computedBoardsFilterValue: {
			immediate: false,
			handler (newVal: BoardsFilter, oldVal: BoardsFilter) {
				if (newVal !== oldVal && this.user && this.team) {
					// Reload team runnits when templates filter changes
					this.loadRunnits(newVal, this.runnitState.runnitsOwnerSelection, this.user, this.team);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
		]),
		handleTemplatesFilterChange (filter: BoardsFilter) {
			this.templatesFilter = filter || BoardsFilter.ALL;
			setRunnitBoardTemplatesFilter(this.templatesFilter);
		},
		loadRunnits (templatesFilter: BoardsFilter, runnitsOwnerSelection: RunnitsOwnerSelection, user: User, team: Team) {
			// Clear and early return if we don't have required data for team mode
			if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && (!user || !team)) {
				this.teamPrivateRunnits = [];
				this.teamRestrictedRunnits = [];
				this.teamSharedRunnits = [];
				return;
			}

			// Clear and early return if we don't have required data for user mode
			if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER && !user) {
				this.personalRunnits = [];
				return;
			}

			// Load featured templates if FEATURED_TEMPLATES filter is active
			if (this.templatesFilter === BoardsFilter.FEATURED_TEMPLATES) {
				this.loadFeaturedRunnits();
				return;
			}

			// Load data if we have what we need
			if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && team && user) {
				// Load only the specific access level for team mode
				this.loadTeamPrivateRunnits(templatesFilter, team, user);
				this.loadTeamRestrictedRunnits(templatesFilter, team, user);
				this.loadTeamSharedRunnits(templatesFilter, team, user);
			} else if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER && user) {
				// Load only personal runnits for personal mode
				this.loadPersonalRunnits(user);
			}
		},
		async loadFeaturedRunnits () {
			this.loadingFeaturedRunnits = true;
			try {
				// Featured templates in the curated order set in Admin > Featured Templates
				// index created: runnits - deletedAt Ascending isFeatured Ascending type Ascending featuredOrder Ascending __name__ Ascending
				const runnitsRef: Query = buildFeaturedTemplatesQuery(
					db.collection('runnits'),
					FEATURED_TEMPLATES_GRID_LIMIT,
				);

				const newResult: Runnit[] = await this.$bind(
					'featuredRunnits',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.featuredRunnits = [];
				}
			} catch (error) {
				console.error('Error loading featured templates:', error);
				this.featuredRunnits = [];
			} finally {
				this.loadingFeaturedRunnits = false;
			}
		},

		async loadPersonalRunnits (user: User) {
			this.loadingPersonalRunnits = true;
			try {
				// Personal runnits - filter by TEMPLATE type
				// index created: runnits - deletedAt Ascending teamId Ascending type Ascending userId Ascending createdAt Descending __name__ Descending
				const runnitsRef: Query = db.collection(`runnits`)
					.where('userId', '==', user.id)
					.where('teamId', '==', null)
					.where('deletedAt', '==', null)
					.where('type', '==', RUNNIT_TYPE.TEMPLATE)
					.orderBy('createdAt', 'desc')
					.limit(4000);

				const newResult: Runnit[] = await this.$bind(
					'personalRunnits',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.personalRunnits = [];
				}
			} catch (error) {
				console.error('Error loading personal templates:', error);
				this.personalRunnits = [];
			} finally {
				this.loadingPersonalRunnits = false;
			}
		},

		async loadTeamPrivateRunnits (templatesFilter: BoardsFilter, team: Team, user: User) {
			this.loadingTeamPrivateRunnits = true;
			try {
				const shouldLoadPrivateTemplates: boolean = !!(
					([
						BoardsFilter.ALL,
						BoardsFilter.MY_BOARDS,
					] as BoardsFilter[]).includes(templatesFilter) ||
					(
						templatesFilter === BoardsFilter.EVERYONES_BOARDS &&
						this.hasManageAllGenerationsPermission
					)
				);
				// Skip loading private templates if filtering out private templates
				if (!shouldLoadPrivateTemplates) {
					this.teamPrivateRunnits = [];
					return;
				}

				// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
				let runnitsRef: Query = db.collection(`runnits`)
					.where('teamId', '==', team.id)
					.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.PRIVATE)
					.where('deletedAt', '==', null)
					.where('type', '==', RUNNIT_TYPE.TEMPLATE)
					.orderBy('createdAt', 'desc')
					.limit(4000);

				// Apply filter based on templatesFilter value
				if (
					([
						BoardsFilter.ALL,
						BoardsFilter.MY_BOARDS,
					] as BoardsFilter[]).includes(templatesFilter)
				) {
					runnitsRef = runnitsRef.where('userId', '==', user.id);
				}

				const newResult: Runnit[] = await this.$bind(
					'teamPrivateRunnits',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.teamPrivateRunnits = [];
				}
			} catch (error) {
				console.error('Error loading team private templates:', error);
				this.teamPrivateRunnits = [];
			} finally {
				this.loadingTeamPrivateRunnits = false;
			}
		},

		async loadTeamRestrictedRunnits (templatesFilter: BoardsFilter, team: Team, user: User) {
			this.loadingTeamRestrictedRunnits = true;
			try {
				const shouldLoadRestrictedTemplates: boolean = !!(
					([
						BoardsFilter.ALL,
						BoardsFilter.MY_BOARDS,
						BoardsFilter.SHARED_WITH_ME,
					] as BoardsFilter[]).includes(templatesFilter) ||
					(
						templatesFilter === BoardsFilter.EVERYONES_BOARDS &&
						this.hasManageAllGenerationsPermission
					)
				);
				// Skip loading restricted templates if filtering out restricted templates
				if (!shouldLoadRestrictedTemplates) {
					this.teamRestrictedRunnits = [];
					return;
				}

				// Team restricted runnits
				// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending type Ascending createdAt Descending __name__ Descending
				let runnitsRef: Query = db.collection(`runnits`)
					.where('teamId', '==', team.id)
					.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.RESTRICTED)
					.where('deletedAt', '==', null)
					.where('type', '==', RUNNIT_TYPE.TEMPLATE)
					// .orderBy('createdAt', 'desc') // TODO: for when I do paging, I can't do orderBy because I can't have an index. (Do I ultimately need to get rid of all these indexes?)
					.limit(4000);

				// Apply filter based on templatesFilter value
				if (templatesFilter === BoardsFilter.MY_BOARDS) {
					// Filter by owner (userId) for MY_BOARDS
					// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending type Ascending userId Ascending createdAt Descending __name__ Descending
					runnitsRef = runnitsRef.where('userId', '==', user.id);
				} else if (
					([
						BoardsFilter.ALL,
						BoardsFilter.SHARED_WITH_ME,
					] as BoardsFilter[]).includes(templatesFilter)
				) {
					runnitsRef = runnitsRef.where(`userIds.${user.id}`, '==', true);
				}

				const newResult: Runnit[] = await this.$bind(
					'teamRestrictedRunnits',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.teamRestrictedRunnits = [];
				}
			} catch (error) {
				console.error('Error loading team restricted templates:', error);
				this.teamRestrictedRunnits = [];
			} finally {
				this.loadingTeamRestrictedRunnits = false;
			}
		},

		async loadTeamSharedRunnits (templatesFilter: BoardsFilter, team: Team, user: User) {
			this.loadingTeamSharedRunnits = true;
			try {
				const shouldLoadSharedTemplates: boolean = !!(
					([
						BoardsFilter.ALL,
						BoardsFilter.MY_BOARDS,
						BoardsFilter.SHARED_WITH_ME,
					] as BoardsFilter[]).includes(templatesFilter) ||
					(
						templatesFilter === BoardsFilter.EVERYONES_BOARDS &&
						this.hasManageAllGenerationsPermission
					)
				);
				// Skip loading shared templates if filtering out shared templates
				if (!shouldLoadSharedTemplates) {
					this.teamSharedRunnits = [];
					return;
				}

				// Team shared runnits
				// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending type Ascending createdAt Descending __name__ Descending
				let runnitsRef: Query = db.collection(`runnits`)
					.where('teamId', '==', team.id)
					.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.SHARED)
					.where('deletedAt', '==', null)
					.where('type', '==', RUNNIT_TYPE.TEMPLATE)
					.orderBy('createdAt', 'desc')
					.limit(4000);

				// Apply filter based on templatesFilter value
				if (templatesFilter === BoardsFilter.MY_BOARDS) {
					// Filter by owner (userId) for MY_BOARDS
					// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending userId Ascending type Ascending createdAt Descending __name__ Descending
					runnitsRef = runnitsRef.where('userId', '==', user.id);
				}

				const newResult: Runnit[] = await this.$bind(
					'teamSharedRunnits',
					runnitsRef,
					get$bindFirestoreOptions(),
				);

				if (!newResult) {
					this.teamSharedRunnits = [];
				}
			} catch (error) {
				console.error('Error loading team shared templates:', error);
				this.teamSharedRunnits = [];
			} finally {
				this.loadingTeamSharedRunnits = false;
			}
		},
	},
	components: {
		RunnitTemplatesManagement,
		RunnitTemplatesOnboardingBanner,
		RunnitTemplatesOnboardingDialog,
	},
});
